import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  catchError,
  filter,
  firstValueFrom,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import {
  _,
  InterpolationParameters,
  TranslateService,
} from '@ngx-translate/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CommonTranslationsPayload } from '@core/types';
import { enUS, ru, tr } from 'date-fns/locale';
import { LocalStorageService } from '@core/services/local-storage.service';
import { PREFERRED_LANG_LOCAL_STORAGE } from '@core/constants';
import { environment } from '@environment/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import bundledRussianTranslations from '../../assets/i18n/ru.json';
import * as Sentry from '@sentry/angular';
import type { SupportedLanguage } from './outkeep-translate.types';

@Injectable({
  providedIn: 'root',
})
export class OutkeepTranslateService {
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly localStorageService = inject(LocalStorageService);
  private failedLanguage: SupportedLanguage | null = null;

  async init(): Promise<void> {
    this.translateService.setTranslation(
      'ru',
      bundledRussianTranslations,
      true,
    );
    const selectedLanguage = this.configureLanguages();

    this.initTranslateRoutesTitles();
    this.initOnlineLanguageRetry();

    await this.activateLanguage(selectedLanguage);
  }

  private configureLanguages(): SupportedLanguage {
    if (!environment.xOutkeepEnableLanguageChange) {
      this.translateService.addLangs(['ru']);
      this.translateService.setFallbackLang('ru');

      return 'ru';
    }
    this.translateService.addLangs(['tr', 'ru', 'en']);

    const savedLang = this.localStorageService.getItem(
      PREFERRED_LANG_LOCAL_STORAGE,
    );

    this.translateService.setFallbackLang('ru');

    if (savedLang === 'en' || savedLang === 'ru' || savedLang === 'tr') {
      return savedLang;
    }

    if (window.navigator.language.includes('en')) {
      return 'en';
    }

    if (window.navigator.language.includes('tr')) {
      return 'tr';
    }

    return 'ru';
  }

  initTranslateRoutesTitles() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;

          return route;
        }),
        mergeMap(route => route.data),
        map(data => data['title']),
        switchMap(titleKey => {
          if (!titleKey) {
            return of('');
          }

          return this.translateService.stream(_(`titles.${titleKey}`));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(translatedTitle => {
        this.titleService.setTitle(translatedTitle);
      });
  }

  public translate(
    keys: (keyof CommonTranslationsPayload)[],
    values?: InterpolationParameters,
  ): Observable<CommonTranslationsPayload> {
    if (values) {
      return this.translateService.stream(_(keys), values);
    }

    return this.translateService.stream(_(keys));
  }

  public setEnglishLang() {
    void this.activateLanguage('en');
  }

  public setRussianLang() {
    void this.activateLanguage('ru');
  }

  public setTurkishLang() {
    void this.activateLanguage('tr');
  }

  public getLang() {
    return this.translateService.getCurrentLang();
  }

  public instant(key: string, values?: InterpolationParameters): string {
    return this.translateService.instant(key, values);
  }

  public onLangChange() {
    return this.translateService.onLangChange;
  }

  public getLocaleStr() {
    if (this.getLang() === 'tr') {
      return 'tr-TR';
    } else if (this.getLang() === 'en') {
      return 'en-Gb';
    } else {
      return 'ru-RU';
    }
  }

  public getLocale() {
    if (this.getLang() === 'tr') {
      return tr;
    } else if (this.getLang() === 'en') {
      return enUS;
    } else {
      return ru;
    }
  }

  private async activateLanguage(language: SupportedLanguage): Promise<void> {
    try {
      await firstValueFrom(this.translateService.use(language));
      this.failedLanguage = null;
    } catch (error) {
      Sentry.captureException(error);
      this.failedLanguage = language === 'ru' ? null : language;

      if (language !== 'ru') {
        await firstValueFrom(this.translateService.use('ru'));
      }
    }
  }

  private initOnlineLanguageRetry(): void {
    const retry = () => {
      const language = this.failedLanguage;

      if (!language) {
        return;
      }

      this.translateService
        .reloadLang(language)
        .pipe(
          switchMap(() => this.translateService.use(language)),
          catchError(error => {
            Sentry.captureException(error);

            return throwError(() => error);
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: () => {
            this.failedLanguage = null;
          },
          error: () => undefined,
        });
    };

    window.addEventListener('online', retry);
    this.destroyRef.onDestroy(() =>
      window.removeEventListener('online', retry),
    );
  }
}
